// Acerto do colaborador (motorista ou agenciador parceiro). A prévia é feita
// no aparelho, com as regras de remuneração (comissão, diárias,
// agenciamentos…), e funciona sem sinal. Fechar, quitar e desfazer passam
// pelo servidor (precisam de internet): ele numera, congela os totais e
// trava o que entrou. Demonstrativo em PDF e assinatura na tela ou pelo link.
import {
  acertos, ajustes, categorias, codigoDoLink, colaboradores, dadosDaPrevia, desfazerAcerto, fecharAcerto, guardarCodigoDoLink,
  prepararFechamento, quitarAcerto, situacoesDeEnvio, veiculos,
} from '../dados.js';
import { montarFechamento, previaDoAcerto, resultadoDoSaldo, temPendencia } from '../lib/acerto.js';
import { assinaturaSuficiente, caminhoDosTracos, desenhoDaAssinatura, novoCodigo, quadroDeAssinatura, sha256Hex } from '../lib/assinatura.js';
import { NOMES_CREDITO, NOMES_DESCONTO, demonstrativoEmPdf, nomeDoArquivoDoAcerto, textoDaQuitacao } from '../lib/demonstrativo.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, lerNumero, placa as formatarPlaca, plural } from '../lib/formato.js';
import { entregarArquivo } from '../lib/recibo.js';
import { puxarAgora } from '../local/sincronia.js';
import { nestaTela } from '../rotas.js';
import { contaGravavel, ehTitular, hojeDaEmpresa, podeGravar, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, campo, carregando, cartaoLink, confirmar, dialogo, emblema, faixa, secao, vazio } from '../ui/componentes.js';
import { emblemaEnvio } from '../ui/sinal.js';
import { faixaDaConta } from './inicio.js';
import { NOME_FORMA, dialogoPagar } from './pagar.js';
import { nomeCurto } from './viagens.js';

const VALIDADE_LINK_DIAS = 30;

// "Viagem de 01/08 a 05/08" ou, se ela estava em andamento na data do
// acerto, "Viagem de 01/08 · parte até 10/08".
export function textoDaViagemNoAcerto(v, acerto) {
  return v.parcial
    ? `Viagem de ${data(v.data_saida)} · parte até ${data(acerto.data)}`
    : `Viagem de ${data(v.data_saida)} a ${data(v.data_chegada)}`;
}

export function periodoDoAcerto(a) {
  return a.periodo_de && a.periodo_de !== a.data ? `${data(a.periodo_de)} a ${data(a.data)}` : data(a.data);
}

// [texto, tipo] da situação do acerto.
export function situacaoDoAcerto(a) {
  if (a.deleted_at) return ['Desfeito', ''];
  if (a.status === 'fechado') return Number(a.saldo) < 0 ? ['A pagar', 'alerta'] : ['A receber', 'alerta'];
  if (a.quitacao === 'transportada') return ['Saldo levado', 'info'];
  if (a.quitacao === 'zerado') return ['Zerado', 'ok'];
  return ['Quitado', 'ok'];
}

export function cartaoAcerto(a, pessoas) {
  const c = pessoas.find((p) => p.id === a.colaborador_id);
  const [texto, tipo] = situacaoDoAcerto(a);
  return cartaoLink({
    href: `#/acertos/${a.id}`,
    simbolo: 'recibo',
    titulo: `Nº ${a.numero}/${a.ano} · ${nomeCurto(c)}`,
    linhas: [periodoDoAcerto(a), a.deleted_at ? `Desfeito: ${a.reaberto_motivo ?? ''}` : resultadoDoSaldo(Number(a.saldo), nomeCurto(c))],
    emblemas: [emblema(texto, tipo), !a.deleted_at && a.assinatura ? emblema('Assinado', 'ok') : null],
  });
}

// Resumo do que está pendente para o colaborador (tela dele, início).
export async function pendenteDoColaborador(colaboradorId, ate = hojeDaEmpresa()) {
  const previa = previaDoAcerto({ colaboradorId, ate, ...(await dadosDaPrevia(colaboradorId)) });
  const f = montarFechamento(previa);
  return { previa, fechamento: f, viagens: f.viagens.length, emAndamento: previa.semNada.length };
}

// Desativado sem nada pendente: depois do último acerto fica só para consulta.
export function soConsulta(c, previa) {
  return Boolean(c) && !c.ativo && !temPendencia(previa) && !previa.bloqueio;
}

// "2 viagens · 3 lançamentos · 1 agenciamento"
function textoPendente(f) {
  const agenciamentos = f.creditos.filter((x) => x.subtipo === 'agenciamento').length;
  return [
    f.viagens.length > 0 && plural(f.viagens.length, 'viagem', 'viagens'),
    f.lancamentos.length > 0 && plural(f.lancamentos.length, 'lançamento', 'lançamentos'),
    agenciamentos > 0 && plural(agenciamentos, 'agenciamento'),
  ].filter(Boolean).join(' · ');
}

// ---------------------------------------------------------------------
// Lista: quem tem acerto a fazer e os acertos feitos
// ---------------------------------------------------------------------
export function telaAcertos() {
  const corpo = h('div', { class: 'secao' }, carregando());

  (async () => {
    const [lista, pessoas] = await Promise.all([acertos.listar(), colaboradores.listar()]);
    const gravavel = podeGravar();
    const aFazer = [];
    const escolhiveis = [];
    // Todos, inclusive os desativados (o motorista que saiu ainda tem o último
    // acerto; depois dele, fica só para consulta).
    for (const p of pessoas) {
      const pend = await pendenteDoColaborador(p.id);
      if (!soConsulta(p, pend.previa)) escolhiveis.push(p);
      if (pend.fechamento.vazio && !pend.previa.bloqueio) continue;
      aFazer.push({ p, pend });
    }
    const aQuitar = lista.filter((a) => a.status === 'fechado');
    const feitos = lista.filter((a) => a.status !== 'fechado');

    trocar(corpo,
      secao('Para acertar', h('div', { class: 'lista' },
        aFazer.map(({ p, pend }) => cartaoLink({
          href: `#/acertos/novo?colaborador=${p.id}`,
          simbolo: 'pessoas',
          titulo: `${nomeCurto(p)}${p.ativo ? '' : ' · desativado'}`,
          linhas: [
            pend.previa.bloqueio ? `O acerto nº ${pend.previa.bloqueio.numero}/${pend.previa.bloqueio.ano} ainda não foi quitado` : textoPendente(pend.fechamento),
            !pend.previa.bloqueio && `Previsto até hoje: ${resultadoDoSaldo(pend.fechamento.totais.saldo, nomeCurto(p))}`,
          ],
        })),
        aFazer.length === 0 && h('p', { class: 'pequeno' }, 'Ninguém com carga, vale ou despesa esperando acerto.')), { contador: String(aFazer.length) }),
      aQuitar.length > 0 && secao('Fechados, esperando quitação', h('div', { class: 'lista' }, aQuitar.map((a) => cartaoAcerto(a, pessoas)))),
      secao('Acertos feitos', h('div', { class: 'lista' },
        feitos.slice(0, 30).map((a) => cartaoAcerto(a, pessoas)),
        feitos.length === 0 && h('p', { class: 'pequeno' }, 'Nenhum acerto fechado ainda.'),
        feitos.length > 30 && h('p', { class: 'pequeno' }, 'Aqui aparecem os 30 mais recentes. Os outros saem no relatório entre datas.'))),
      h('div', { class: 'acoes' },
        h('a', { class: 'botao cheio secundario', href: '#/acertos/relatorio' }, 'Relatório de acertos entre datas'),
        gravavel && escolhiveis.length > 0 && campo({
          rotulo: 'Fazer o acerto de',
          nome: 'escolher_colaborador',
          tipo: 'select',
          opcoes: [['', 'Escolha a pessoa'], ...escolhiveis.map((p) => [p.id, `${p.apelido ? `${p.nome} (${p.apelido})` : p.nome}${p.ativo ? '' : ' · desativado'}`])],
          aoMudar: (e) => {
            if (e.target.value) location.hash = `#/acertos/novo?colaborador=${e.target.value}`;
          },
        })),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo: 'Acertos', voltar: '/colaboradores', conteudo: [faixaDaConta(), corpo] });
}

// ---------------------------------------------------------------------
// Prévia e fechamento
// ---------------------------------------------------------------------
// O que a pessoa marcou ou desmarcou, por colaborador (sobrevive a um
// redesenho da tela).
const escolhas = new Map();

function itensDaPrevia(previa) {
  return [
    ...previa.viagens.map((x) => [`v:${x.viagem.id}`, x]),
    ...previa.agenciamentos.map((x) => [`a:${x.credito.frete_id}`, x]),
    ...previa.fixos.map((x) => [`f:${x.credito.data}`, x]),
    ...[...previa.vales, ...previa.despesas, ...previa.creditos, ...previa.descontos].map((x) => [`l:${x.lancamento.id}`, x]),
  ];
}

function aplicarEscolhas(previa, marcas) {
  for (const [chave, item] of itensDaPrevia(previa)) {
    if (marcas.has(chave)) item.incluir = marcas.get(chave);
  }
}

export function telaAcertoNovo({ busca }) {
  const tela = nestaTela();
  const colaboradorId = busca.get('colaborador');
  const hoje = hojeDaEmpresa();
  const pedida = busca.get('ate');
  const ate = /^\d{4}-\d{2}-\d{2}$/.test(pedida ?? '') && pedida <= hoje ? pedida : hoje;
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, 'Acerto');

  (async () => {
    const [c, frota, cats, dados] = await Promise.all([
      colaboradores.obter(colaboradorId), veiculos.listar(), categorias.todas(), dadosDaPrevia(colaboradorId),
    ]);
    if (!c) {
      trocar(corpo, vazio({ simbolo: 'pessoas', titulo: 'Colaborador não encontrado', acao: h('a', { class: 'botao', href: '#/acertos' }, 'Ver acertos') }));
      return;
    }
    const nome = nomeCurto(c);
    titulo.textContent = `Acerto de ${nome}`;
    const gravavel = podeGravar();
    if (!escolhas.has(c.id)) escolhas.set(c.id, new Map());
    const marcas = escolhas.get(c.id);
    const previa = previaDoAcerto({ colaboradorId: c.id, ate, ...dados });
    if (soConsulta(c, previa)) {
      trocar(corpo,
        faixa(`${nome} está desativado e não tem nada para acertar: depois do último acerto, fica só para consulta.`, 'info', 'Só para consulta'),
        h('div', { class: 'acoes' }, h('a', { class: 'botao secundario', href: `#/colaboradores/${c.id}` }, `Ver os acertos de ${nome}`)));
      return;
    }
    aplicarEscolhas(previa, marcas);
    const nomeCat = (id) => cats.find((x) => x.id === id)?.nome ?? 'Despesa';
    const viagemDe = (id) => dados.viagens.find((v) => v.id === id);

    const form = h('form', { class: 'previa-acerto', novalidate: true, onSubmit: (e) => e.preventDefault() });
    const mexeu = () => {
      form.dataset.alterado = '1';
    };
    // Mexeu (marcou, desmarcou, escreveu): a tela não é redesenhada sozinha
    // quando chegam dados do servidor.
    form.addEventListener('input', mexeu);
    form.addEventListener('change', mexeu);
    const resumo = h('div', { class: 'resumo-acerto', 'aria-live': 'polite' });
    const observacao = campo({ rotulo: 'Observação do acerto', nome: 'observacao', tipo: 'textarea', dica: 'Sai no demonstrativo.' });
    const botaoFechar = h('button', { class: 'botao cheio', type: 'button', onClick: (e) => fechar(e.currentTarget) }, 'Fechar acerto');
    const travado = !gravavel || Boolean(previa.bloqueio);

    function item({ chave, alvo, titulo: texto, linhas = [], valor = null, extra = null, aoAlternar = null }) {
      const caixa = h('input', { type: 'checkbox', checked: alvo.incluir, disabled: travado });
      const rotulo = h('label', { class: `item-acerto${alvo.incluir ? '' : ' fora'}` },
        caixa,
        h('span', { class: 'item-corpo' },
          h('span', { class: 'item-titulo' }, texto),
          linhas.filter(Boolean).map((l) => h('span', { class: 'item-linha' }, l))),
        valor !== null && h('span', { class: 'item-valor' }, dinheiro(valor)));
      caixa.addEventListener('change', () => {
        alvo.incluir = caixa.checked;
        marcas.set(chave, caixa.checked);
        rotulo.classList.toggle('fora', !caixa.checked);
        mexeu();
        if (aoAlternar) aoAlternar(caixa.checked);
        else atualizarResumo();
      });
      return h('div', { class: 'item-acerto-linha' }, rotulo, extra);
    }

    function atualizarResumo() {
      const f = montarFechamento(previa);
      const t = f.totais;
      trocar(resumo,
        h('dl', { class: 'linha-dados resumo' },
          t.saldoAnterior !== 0 && [h('dt', null, 'Saldo do acerto anterior'), h('dd', null, dinheiro(t.saldoAnterior))],
          h('dt', null, '+ Vales'), h('dd', null, dinheiro(t.vales)),
          h('dt', null, '− Despesas pagas por ele'), h('dd', null, dinheiro(t.despesas)),
          h('dt', null, '− Créditos'), h('dd', null, dinheiro(t.creditos)),
          h('dt', null, '+ Descontos'), h('dd', null, dinheiro(t.descontos)),
          h('dt', { class: 'total' }, '= Saldo'), h('dd', { class: 'total' }, dinheiro(t.saldo))),
        h('p', { class: `resultado-acerto${t.saldo < 0 ? ' paga' : t.saldo > 0 ? ' devolve' : ''}` }, resultadoDoSaldo(t.saldo, nome)));
      botaoFechar.disabled = travado || f.vazio;
    }

    // Ao desmarcar a viagem, os vales e as despesas dela saem junto (e voltam
    // ao marcar); dá para mexer um a um depois.
    function alternarViagem(viagemId, sim) {
      for (const lista of [previa.vales, previa.despesas, previa.creditos, previa.descontos]) {
        for (const x of lista) {
          if (x.lancamento.viagem_id === viagemId) {
            x.incluir = sim;
            marcas.set(`l:${x.lancamento.id}`, sim);
          }
        }
      }
      desenhar();
    }

    async function excluirAjuste(l) {
      const ok = await confirmar({ titulo: 'Excluir este lançamento?', texto: 'Ele vai para a lixeira e sai do acerto.', sim: 'Excluir', perigo: true });
      if (!ok) return;
      try {
        await ajustes.excluir(l.id);
        avisar('Lançamento excluído.');
        tela.renderizar();
      } catch (erro) {
        avisar(mensagemDeErro(erro), 'erro');
      }
    }

    async function novoAjuste(tipo) {
      const r = await dialogoAjuste(tipo, ate);
      if (!r) return;
      try {
        await ajustes.criar({ tipo, colaborador_id: c.id, ...r });
        avisar(tipo === 'credito' ? 'Crédito lançado.' : 'Desconto lançado.');
        tela.renderizar();
      } catch (erro) {
        avisar(mensagemDeErro(erro), 'erro');
      }
    }

    function desenhar() {
      const rolagem = window.scrollY;
      const soma = (lista) => lista.reduce((s, x) => s + Math.round(Number(x.valor) * 100), 0) / 100;
      const viagemTexto = (v) => (v ? `Viagem de ${data(v.data_saida)}` : null);

      const temViagem = previa.viagens.length + previa.semNada.length + previa.depois.length > 0;
      const blocoViagens = (temViagem || c.funcao === 'motorista') && secao('Viagens', h('div', { class: 'lista' },
        previa.viagens.map((pv) => {
          const v = pv.viagem;
          const caminhao = frota.find((x) => x.id === v.veiculo_id);
          const bruto = pv.cargas.reduce((s, f) => s + Number(f.valor_frete || 0), 0);
          const quando = pv.fecha ? `${data(v.data_saida)} a ${data(v.data_chegada)}`
            : `${data(v.data_saida)} · ${v.status === 'aberta' ? 'em andamento' : `voltou em ${data(v.data_chegada)}`}`;
          return item({
            chave: `v:${v.id}`,
            alvo: pv,
            titulo: `${quando} · ${formatarPlaca(caminhao?.placa ?? '')}`,
            linhas: [
              pv.cargas.length > 0 ? `${plural(pv.cargas.length, 'carga')} · frete ${dinheiro(bruto)}` : 'Nenhuma carga nova até esta data',
              !pv.fecha && `Entra o que foi carregado até ${data(ate)}; o que vier depois fica para o próximo acerto.`,
              pv.jaAcertouParte && 'Parte desta viagem já entrou num acerto anterior.',
              ...pv.creditos.map((cr) => `${cr.descricao}: ${dinheiro(cr.valor)}`),
              pv.creditos.length === 0 && pv.cargas.length > 0 && 'Nenhum crédito pelas regras de remuneração cadastradas.',
            ],
            valor: soma(pv.creditos),
            aoAlternar: (sim) => alternarViagem(v.id, sim),
          });
        }),
        previa.viagens.length === 0 && h('p', { class: 'pequeno' }, 'Nenhuma viagem com carga esperando acerto.'),
        previa.semNada.map((v) => h('p', { class: 'pequeno' },
          `Viagem de ${data(v.data_saida)} em andamento: nenhuma carga até ${data(ate)}.`)),
        previa.depois.map((v) => h('p', { class: 'pequeno' },
          `Viagem de ${data(v.data_saida)}: saiu depois de ${data(ate)} e fica para o próximo acerto.`))),
      { contador: previa.viagens.length ? String(previa.viagens.filter((x) => x.incluir).length) : null });

      const blocoAgenciamentos = previa.agenciamentos.length > 0 && secao('Agenciamentos', h('div', { class: 'lista' },
        previa.agenciamentos.map((a) => item({
          chave: `a:${a.credito.frete_id}`,
          alvo: a,
          titulo: a.credito.descricao,
          linhas: [a.frete?.adto_recebido_em ? `Adiantamento recebido em ${data(a.frete.adto_recebido_em)}`
            : Number(a.frete?.adto_valor) > 0 ? 'Adiantamento do frete ainda não recebido' : null],
          valor: a.credito.valor,
        }))));

      const blocoFixos = previa.fixos.length > 0 && secao('Fixo mensal', h('div', { class: 'lista' },
        previa.fixos.map((f) => item({ chave: `f:${f.credito.data}`, alvo: f, titulo: f.credito.descricao, valor: f.credito.valor }))));

      const blocoVales = secao('Vales (ele já recebeu)', h('div', { class: 'lista' },
        previa.vales.map((x) => item({
          chave: `l:${x.lancamento.id}`,
          alvo: x,
          titulo: `${data(x.lancamento.data)}${x.lancamento.forma ? ` · ${NOME_FORMA[x.lancamento.forma] ?? x.lancamento.forma}` : ''}`,
          linhas: [viagemTexto(viagemDe(x.lancamento.viagem_id)), x.lancamento.descricao],
          valor: Number(x.lancamento.valor),
        })),
        previa.vales.length === 0 && h('p', { class: 'pequeno' }, 'Nenhum vale esperando acerto.')));

      const blocoDespesas = previa.despesas.length > 0 && secao('Despesas pagas por ele (a empresa devolve)', h('div', { class: 'lista' },
        previa.despesas.map((x) => item({
          chave: `l:${x.lancamento.id}`,
          alvo: x,
          titulo: `${data(x.lancamento.data)} · ${nomeCat(x.lancamento.categoria_id)}`,
          linhas: [viagemTexto(viagemDe(x.lancamento.viagem_id)), x.lancamento.fornecedor, x.lancamento.descricao],
          valor: Number(x.lancamento.valor),
        }))));

      const ajuste = (x, nomes) => item({
        chave: `l:${x.lancamento.id}`,
        alvo: x,
        titulo: `${data(x.lancamento.data)} · ${x.lancamento.origem === 'implantacao' ? 'Saldo de implantação' : nomes[x.lancamento.subtipo] ?? x.lancamento.subtipo}`,
        linhas: [x.lancamento.descricao],
        valor: Number(x.lancamento.valor),
        // O que o sistema ou a importação lançou não se exclui daqui.
        extra: gravavel && !['sistema', 'implantacao', 'importacao'].includes(x.lancamento.origem) && h('button', {
          class: 'botao texto pequeno-botao',
          type: 'button',
          'aria-label': 'Excluir este lançamento',
          onClick: () => excluirAjuste(x.lancamento),
        }, 'Excluir'),
      });
      const blocoAjustes = secao('Outros créditos e descontos', h('div', { class: 'lista' },
        previa.creditos.map((x) => ajuste(x, NOMES_CREDITO)),
        previa.descontos.map((x) => ajuste(x, NOMES_DESCONTO)),
        previa.creditos.length + previa.descontos.length === 0 && h('p', { class: 'pequeno' }, 'Bônus, avaria, multa… lance aqui o que não sai das viagens.'),
        gravavel && h('div', { class: 'acoes-lado' },
          h('button', { class: 'botao secundario', type: 'button', onClick: () => novoAjuste('credito') }, '+ Crédito'),
          h('button', { class: 'botao secundario', type: 'button', onClick: () => novoAjuste('desconto') }, '+ Desconto'))));

      trocar(form,
        blocoViagens, blocoAgenciamentos, blocoFixos, blocoVales, blocoDespesas, blocoAjustes,
        secao('Resumo', resumo),
        observacao,
        h('div', { class: 'acoes' },
          botaoFechar,
          h('p', { class: 'pequeno' }, 'Fechar precisa de internet. Depois de fechado, o que entrou no acerto fica travado; só o titular desfaz.')));
      atualizarResumo();
      window.scrollTo(0, rolagem);
    }

    async function fechar(botao) {
      const f = montarFechamento(previa);
      if (f.vazio) return;
      const ok = await confirmar({
        titulo: `Fechar o acerto de ${nome}?`,
        texto: `${resultadoDoSaldo(f.totais.saldo, nome)} Depois de fechado, o que entrou no acerto não muda mais (só o titular desfaz).`,
        sim: 'Fechar acerto',
      });
      if (!ok) return;
      botao.disabled = true;
      const antes = botao.textContent;
      botao.textContent = 'Fechando…';
      try {
        // A prévia de novo, com os dados mais novos do servidor e as mesmas marcações.
        await prepararFechamento();
        const nova = previaDoAcerto({ colaboradorId: c.id, ate, ...(await dadosDaPrevia(c.id)) });
        aplicarEscolhas(nova, marcas);
        const final = montarFechamento(nova);
        const mesma = final.totais.saldo === f.totais.saldo
          && JSON.stringify(final.creditos) === JSON.stringify(f.creditos)
          && JSON.stringify([...final.lancamentos].sort()) === JSON.stringify([...f.lancamentos].sort());
        if (!mesma) {
          avisar('Chegaram dados novos do servidor e o acerto mudou. Confira de novo antes de fechar.', 'erro');
          tela.renderizar();
          return;
        }
        const r = await fecharAcerto({
          colaboradorId: c.id, data: ate, fechamento: final, observacao: form.querySelector('[name="observacao"]').value.trim() || null,
        });
        escolhas.delete(c.id);
        avisar(`Acerto nº ${r.numero}/${r.ano} fechado.`);
        tela.ir(`/acertos/${r.id}`, { substituir: true });
      } catch (erro) {
        avisar(mensagemDeErro(erro), 'erro');
        if (botao.isConnected) {
          botao.disabled = false;
          botao.textContent = antes;
        }
      }
    }

    const topo = [
      previa.bloqueio && faixa(h('span', null,
        `O acerto nº ${previa.bloqueio.numero}/${previa.bloqueio.ano} ainda não foi quitado. `,
        h('a', { href: `#/acertos/${previa.bloqueio.id}` }, 'Abra o acerto'),
        ' e registre o pagamento, a devolução, ou leve o saldo para o próximo acerto.'), 'alerta', 'Acerto anterior em aberto'),
      !previa.bloqueio && dados.ultimoAcerto && h('p', { class: 'pequeno' },
        `Último acerto: nº ${dados.ultimoAcerto.numero}/${dados.ultimoAcerto.ano}, de ${data(dados.ultimoAcerto.data)}.`
        + (previa.saldoAnterior < 0 ? ` A empresa ficou devendo ${dinheiro(-previa.saldoAnterior)} nele; esse valor entra neste acerto.` : '')
        + (previa.saldoAnterior > 0 ? ` ${nome} ficou devendo ${dinheiro(previa.saldoAnterior)} nele; esse valor entra neste acerto.` : '')),
      campo({
        rotulo: 'Acerto até',
        nome: 'ate',
        tipo: 'date',
        valor: ate,
        atributos: { max: hoje },
        dica: 'Entram as cargas carregadas, os vales e as despesas até esta data, também da viagem em andamento.',
        aoMudar: (e) => {
          if (e.target.value && e.target.value <= hoje) tela.ir(`/acertos/novo?colaborador=${c.id}&ate=${e.target.value}`, { substituir: true });
        },
      }),
    ];
    trocar(corpo, topo, form);
    desenhar();
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return casca({ titulo, voltar: colaboradorId ? `/colaboradores/${colaboradorId}` : '/acertos', conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
}

// Crédito (bônus…) ou desconto (avaria, multa…) lançado à mão.
function dialogoAjuste(tipo, ate) {
  const opcoes = tipo === 'credito'
    ? [['bonus', 'Bônus'], ['outro', 'Outro crédito']]
    : [['avaria', 'Avaria'], ['multa', 'Multa'], ['outro', 'Outro desconto']];
  return dialogo((fechar) => {
    const subtipo = h('select', { id: 'ajuste-tipo' }, opcoes.map(([v, t]) => h('option', { value: v }, t)));
    const valor = h('input', { type: 'text', id: 'ajuste-valor', inputmode: 'decimal', placeholder: '0,00' });
    const quando = h('input', { type: 'date', id: 'ajuste-data', value: ate, max: ate });
    const descricao = h('input', { type: 'text', id: 'ajuste-descricao', maxlength: '200' });
    const erro = h('p', { class: 'erro-campo', role: 'alert' });
    return [
      h('h2', null, tipo === 'credito' ? 'Novo crédito' : 'Novo desconto'),
      h('p', null, tipo === 'credito' ? 'O que ele ganhou além das viagens (bônus, ajuda…).' : 'O que ele deve à empresa (avaria, multa…).'),
      h('div', { class: 'campo' }, h('label', { for: 'ajuste-tipo' }, 'Tipo'), subtipo),
      h('div', { class: 'grade-2' },
        h('div', { class: 'campo' }, h('label', { for: 'ajuste-valor' }, 'Valor (R$)'), valor),
        h('div', { class: 'campo' }, h('label', { for: 'ajuste-data' }, 'Data'), quando)),
      h('div', { class: 'campo' }, h('label', { for: 'ajuste-descricao' }, 'Descrição'), descricao),
      erro,
      h('div', { class: 'acoes' },
        h('button', {
          class: 'botao cheio',
          type: 'button',
          onClick: () => {
            const v = lerNumero(valor.value);
            if (!(v > 0)) {
              erro.textContent = 'Informe o valor, como 150,00.';
              return;
            }
            if (!quando.value || quando.value > ate) {
              erro.textContent = 'A data precisa ser até a data do acerto.';
              return;
            }
            if (subtipo.value === 'outro' && descricao.value.trim().length < 3) {
              erro.textContent = 'Descreva o que é.';
              return;
            }
            fechar({ subtipo: subtipo.value, valor: v, data: quando.value, descricao: descricao.value.trim() || null });
          },
        }, 'Lançar'),
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(null) }, 'Cancelar')),
    ];
  });
}

// ---------------------------------------------------------------------
// Acerto fechado
// ---------------------------------------------------------------------
export function enderecoDoLinkDoAcerto(codigo) {
  return `${location.origin}${location.pathname}#/assinar-acerto/${codigo}`;
}

export function telaAcerto({ params }) {
  const tela = nestaTela();
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, 'Acerto');
  const pagina = casca({ titulo, voltar: '/acertos', conteudo: [contaGravavel() ? null : faixaDaConta(), corpo] });
  const linkVoltar = pagina.querySelector('.topo a[aria-label="Voltar"]');

  (async () => {
    const a = await acertos.obter(params.id);
    if (!a) {
      trocar(corpo, vazio({ titulo: 'Acerto não encontrado', acao: h('a', { class: 'botao', href: '#/acertos' }, 'Ver acertos') }));
      return;
    }
    const [c, itens, frota, cats, envio] = await Promise.all([
      colaboradores.obter(a.colaborador_id), acertos.itens(a.id), veiculos.listar(), categorias.todas(), situacoesDeEnvio('acertos'),
    ]);
    const nome = nomeCurto(c);
    titulo.textContent = `Acerto nº ${a.numero}/${a.ano}`;
    linkVoltar?.setAttribute('href', `#/colaboradores/${a.colaborador_id}`);
    const gravavel = podeGravar() && !a.deleted_at;
    const hoje = hojeDaEmpresa();
    const [situacao, tipoSituacao] = situacaoDoAcerto(a);
    const saldo = Number(a.saldo);
    const lancs = itens.lancamentos;
    const pagamento = lancs.find((l) => l.tipo === 'pagamento' || l.tipo === 'devolucao') ?? null;
    const doTipo = (tipo) => lancs.filter((l) => l.tipo === tipo);
    const nomeCat = (id) => cats.find((x) => x.id === id)?.nome ?? 'Despesa';

    const executar = async (acao, sucesso, depois = null) => {
      try {
        await acao();
        avisar(sucesso);
        if (depois) tela.ir(depois, { substituir: true });
        else tela.renderizar();
      } catch (erro) {
        avisar(mensagemDeErro(erro), 'erro');
      }
    };

    const cabecalho = h('div', { class: 'cartao destaque' }, h('div', { class: 'cartao-corpo' },
      h('span', { class: 'cartao-titulo' }, `${c?.nome ?? ''} · ${periodoDoAcerto(a)}`),
      h('span', { class: 'valor-grande' }, dinheiro(Math.abs(saldo))),
      h('span', { class: 'cartao-linha' }, resultadoDoSaldo(saldo, nome)),
      h('span', { class: 'cartao-linha' }, pagamento && !a.deleted_at
        ? `${pagamento.tipo === 'pagamento' ? 'Pago' : 'Devolvido'} em ${data(pagamento.data)}${pagamento.forma ? ` · ${NOME_FORMA[pagamento.forma] ?? pagamento.forma}` : ''}.`
        : textoDaQuitacao(a)),
      h('span', { class: 'cartao-emblemas' },
        emblema(situacao, tipoSituacao),
        a.assinatura ? emblema('Assinado', 'ok') : a.assinatura_token_hash ? emblema('Link mandado', 'info') : emblema('Sem assinatura'),
        emblemaEnvio(envio.get(a.id)))));

    const resumo = h('dl', { class: 'linha-dados resumo' },
      Number(a.saldo_anterior) !== 0 && [h('dt', null, 'Saldo do acerto anterior'), h('dd', null, dinheiro(a.saldo_anterior))],
      h('dt', null, '+ Vales'), h('dd', null, dinheiro(a.total_vales)),
      h('dt', null, '− Despesas pagas por ele'), h('dd', null, dinheiro(a.total_despesas)),
      h('dt', null, '− Créditos'), h('dd', null, dinheiro(a.total_creditos)),
      h('dt', null, '+ Descontos'), h('dd', null, dinheiro(a.total_descontos)),
      h('dt', { class: 'total' }, '= Saldo'), h('dd', { class: 'total' }, dinheiro(a.saldo)));

    // Quitação (a empresa pagou, ele devolveu, ou o saldo vai para o próximo)
    let blocoQuitacao = null;
    if (gravavel && a.status === 'fechado') {
      const pagar = async () => {
        const r = await dialogoPagar({
          titulo: saldo < 0 ? `Pagamento a ${nome}` : `Devolução de ${nome}`,
          detalhe: `Acerto nº ${a.numero}/${a.ano}`,
          valor: Math.abs(saldo),
        });
        if (r) executar(() => quitarAcerto(a.id, { como: saldo < 0 ? 'paga' : 'devolvida', data: r.pago_em, forma: r.forma }), 'Quitação registrada.');
      };
      const levar = async () => {
        const ok = await confirmar({
          titulo: 'Levar o saldo para o próximo acerto?',
          texto: `${resultadoDoSaldo(saldo, nome)} Esse valor entra como saldo anterior no próximo acerto de ${nome}.`,
          sim: 'Levar o saldo',
        });
        if (ok) executar(() => quitarAcerto(a.id, { como: 'transportada', data: hoje }), 'O saldo vai para o próximo acerto.');
      };
      blocoQuitacao = secao('Quitação', h('div', { class: 'acoes' },
        h('button', { class: 'botao cheio', type: 'button', onClick: pagar }, saldo < 0 ? `Registrar o pagamento a ${nome}` : `Registrar a devolução de ${nome}`),
        c?.ativo
          ? h('button', { class: 'botao cheio secundario', type: 'button', onClick: levar }, 'Levar o saldo para o próximo acerto')
          : h('p', { class: 'pequeno' }, `${nome} está desativado: este é o último acerto, então o saldo não vai para um próximo.`),
        h('p', { class: 'pequeno' }, 'Precisa de internet.')));
    }

    const blocoAssinatura = !a.deleted_at && await blocoDemonstrativo({ a, c, itens, frota, cats, pagamento, gravavel: podeGravar(), envio: envio.get(a.id), tela });

    const linha = (texto, valor) => h('div', { class: 'linha-valor' }, h('span', null, texto), h('span', null, dinheiro(valor)));
    const blocoItens = secao('O que entrou', h('div', { class: 'lista' },
      itens.viagens.map((v) => {
        const cargas = itens.fretes.filter((f) => f.viagem_id === v.id);
        return cartaoLink({
          href: `#/viagens/${v.id}`,
          simbolo: 'caminhao',
          titulo: textoDaViagemNoAcerto(v, a),
          linhas: [
            formatarPlaca(frota.find((x) => x.id === v.veiculo_id)?.placa ?? ''),
            cargas.length > 0 && `${plural(cargas.length, 'carga')} neste acerto · frete ${dinheiro(cargas.reduce((s2, f) => s2 + Number(f.valor_frete || 0), 0))}`,
          ],
        });
      }),
      doTipo('credito').length > 0 && h('div', { class: 'grupo-valores' }, h('h3', null, 'Créditos'),
        doTipo('credito').map((l) => linha(`${data(l.data)} · ${l.descricao || NOMES_CREDITO[l.subtipo] || 'Crédito'}`, l.valor))),
      doTipo('vale').length > 0 && h('div', { class: 'grupo-valores' }, h('h3', null, 'Vales'),
        doTipo('vale').map((l) => linha(`${data(l.data)}${l.forma ? ` · ${NOME_FORMA[l.forma] ?? l.forma}` : ''}${l.descricao ? ` · ${l.descricao}` : ''}`, l.valor))),
      doTipo('despesa').length > 0 && h('div', { class: 'grupo-valores' }, h('h3', null, 'Despesas pagas por ele'),
        doTipo('despesa').map((l) => linha(`${data(l.data)} · ${[nomeCat(l.categoria_id), l.fornecedor].filter(Boolean).join(' · ')}`, l.valor))),
      doTipo('desconto').length > 0 && h('div', { class: 'grupo-valores' }, h('h3', null, 'Descontos'),
        doTipo('desconto').map((l) => linha(`${data(l.data)} · ${l.descricao || NOMES_DESCONTO[l.subtipo] || 'Desconto'}`, l.valor))),
      a.observacao && h('p', { class: 'pequeno' }, `Observação: ${a.observacao}`)));

    const desfazer = async () => {
      const motivo = await dialogoDesfazer(a);
      if (motivo) executar(() => desfazerAcerto(a.id, motivo), 'Acerto desfeito. O que estava nele voltou a ficar pendente.', `/acertos/novo?colaborador=${a.colaborador_id}`);
    };

    trocar(corpo,
      a.deleted_at && faixa(`Este acerto foi desfeito. Motivo: ${a.reaberto_motivo ?? '—'}`, 'alerta', 'Acerto desfeito'),
      cabecalho,
      secao('Resumo', resumo),
      blocoQuitacao,
      blocoAssinatura,
      a.deleted_at ? null : blocoItens,
      gravavel && ehTitular() && h('div', { class: 'acoes' },
        h('button', { class: 'botao texto', type: 'button', onClick: desfazer }, 'Desfazer este acerto')),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa(mensagemDeErro(erro), 'perigo'));
  });

  return pagina;
}

function dialogoDesfazer(a) {
  return dialogo((fechar) => {
    const motivo = h('textarea', { id: 'desfazer-motivo', rows: 3, maxlength: '300' });
    const erro = h('p', { class: 'erro-campo', role: 'alert' });
    return [
      h('h2', null, `Desfazer o acerto nº ${a.numero}/${a.ano}?`),
      h('p', null, 'As viagens, os vales e as despesas dele voltam a ficar pendentes, e os créditos calculados e o pagamento vão para a lixeira. Depois, faça o acerto de novo (ele ganha outro número).'),
      h('div', { class: 'campo' }, h('label', { for: 'desfazer-motivo' }, 'Por que está desfazendo?'), motivo),
      erro,
      h('div', { class: 'acoes' },
        h('button', {
          class: 'botao cheio perigo-cheio',
          type: 'button',
          onClick: () => {
            if (motivo.value.trim().length < 3) {
              erro.textContent = 'Escreva o motivo.';
              return;
            }
            fechar(motivo.value.trim());
          },
        }, 'Desfazer acerto'),
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => fechar(null) }, 'Cancelar')),
    ];
  });
}

// Demonstrativo em PDF e assinatura (na tela ou pelo link).
async function blocoDemonstrativo({ a, c, itens, frota, cats, pagamento, gravavel, envio, tela }) {
  const registro = await codigoDoLink(a.id);
  const linkPedido = Boolean(a.assinatura_token_hash);
  const faltaEnviar = envio === 'pendente' || envio === 'recusada';
  const nome = nomeCurto(c);
  const primeiroNome = (c?.apelido || c?.nome || '').split(' ')[0];

  const executar = async (acao, sucesso) => {
    try {
      await acao();
      if (sucesso) avisar(sucesso);
      tela.renderizar();
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
    }
  };

  const pdf = async (botao) => {
    botao.disabled = true;
    try {
      const blob = await demonstrativoEmPdf({
        acerto: a, empresa: sessao.empresa, colaborador: c, viagens: itens.viagens, veiculos: frota, lancamentos: itens.lancamentos, categorias: cats, pagamento,
      });
      const r = await entregarArquivo(blob, nomeDoArquivoDoAcerto({ acerto: a, colaborador: c }), `Acerto nº ${a.numero}/${a.ano} de ${nome}`);
      if (r === 'baixado') avisar('Demonstrativo baixado em PDF.');
    } catch (erro) {
      console.error(erro);
      avisar('Não foi possível gerar o PDF agora. Tente de novo.', 'erro');
    } finally {
      botao.disabled = false;
    }
  };

  const link = async () => {
    const vencido = a.assinatura_pedida_em && Date.now() - new Date(a.assinatura_pedida_em).getTime() > (VALIDADE_LINK_DIAS - 1) * 86400000;
    if (registro && registro.hash === a.assinatura_token_hash && !vencido) return enderecoDoLinkDoAcerto(registro.codigo);
    const codigo = novoCodigo();
    const hash = await sha256Hex(codigo);
    await guardarCodigoDoLink(a.id, { codigo, hash, criado_em: new Date().toISOString() });
    await acertos.atualizar(a.id, { assinatura_token_hash: hash }, null, { esperarServidor: false });
    return enderecoDoLinkDoAcerto(codigo);
  };

  const mensagem = (url) => `Olá, ${primeiroNome}! Fechei o acerto nº ${a.numero}/${a.ano} (${periodoDoAcerto(a)}): `
    + `${resultadoDoSaldo(Number(a.saldo), 'você').replace('a você', 'para você')} Confira e assine pelo link: ${url}`;

  const pedirPeloWhatsApp = () => executar(async () => {
    const url = await link();
    const texto = encodeURIComponent(mensagem(url));
    const destino = c?.celular ? `https://wa.me/${c.celular}?text=${texto}` : `https://wa.me/?text=${texto}`;
    const alvo = h('a', { href: destino, target: '_blank', rel: 'noopener' });
    document.body.append(alvo);
    alvo.click();
    alvo.remove();
  }, null);

  const copiarLink = () => executar(async () => {
    const url = await link();
    try {
      await navigator.clipboard.writeText(url);
      avisar('Link copiado. Cole na conversa com a pessoa.');
    } catch {
      await dialogo((fechar) => [
        h('h2', null, 'Link para assinar'),
        h('p', null, 'Copie e mande para a pessoa:'),
        h('input', { type: 'text', value: url, readOnly: true, class: 'link-copiar', onFocus: (e) => e.target.select() }),
        h('div', { class: 'acoes' }, h('button', { class: 'botao cheio', type: 'button', onClick: () => fechar(null) }, 'Fechar')),
      ]);
    }
  }, null);

  const assinarNaTela = async () => {
    const caminho = await dialogoAssinarAcerto(a, c);
    if (caminho) {
      executar(() => acertos.atualizar(a.id, { assinatura: caminho, assinado_em: new Date().toISOString(), assinado_via: 'presencial' }), 'Assinatura guardada.');
    }
  };

  const apagarAssinatura = async () => {
    const ok = await confirmar({
      titulo: 'Apagar a assinatura?',
      texto: 'Use quando a assinatura saiu errada. A pessoa assina de novo; o link antigo deixa de valer.',
      sim: 'Apagar assinatura',
      perigo: true,
    });
    if (ok) executar(() => acertos.atualizar(a.id, { assinatura: null, assinado_em: null, assinado_via: null, assinatura_token_hash: null }), 'Assinatura apagada.');
  };

  let situacao;
  if (a.assinatura) {
    situacao = [
      desenhoDaAssinatura(a.assinatura),
      h('p', { class: 'pequeno' }, `${c?.nome ?? ''} assinou ${a.assinado_via === 'link' ? 'pelo link, no celular dele' : 'na tela'}.`),
    ];
  } else if (linkPedido) {
    situacao = faixa(
      faltaEnviar
        ? 'O link já foi feito, mas ainda não chegou ao servidor: ele só abre depois que o sinal voltar.'
        : 'Link mandado. Quando a pessoa assinar, a assinatura aparece aqui.',
      'info', 'Esperando a assinatura');
  } else {
    situacao = h('p', { class: 'pequeno' }, 'Ainda sem assinatura. Se a pessoa estiver aí, ela assina na tela; se estiver longe, mande o link pelo WhatsApp.');
  }

  const bloco = secao('Demonstrativo e assinatura', h('div', { class: 'lista' },
    situacao,
    h('div', { class: 'acoes' },
      h('button', { class: 'botao cheio', type: 'button', onClick: (e) => pdf(e.currentTarget) }, 'Demonstrativo em PDF'),
      gravavel && !a.assinatura && h('button', { class: 'botao cheio secundario', type: 'button', onClick: assinarNaTela }, 'Assinar na tela agora'),
      gravavel && !a.assinatura && h('button', { class: 'botao cheio secundario', type: 'button', onClick: pedirPeloWhatsApp }, 'Pedir assinatura pelo WhatsApp'),
      gravavel && !a.assinatura && h('button', { class: 'botao texto', type: 'button', onClick: copiarLink }, 'Copiar o link de assinatura'),
      gravavel && a.assinatura && h('button', { class: 'botao texto', type: 'button', onClick: apagarAssinatura }, 'Apagar assinatura'))));

  // Esperando a assinatura pelo link: confere de tempos em tempos.
  if (linkPedido && !a.assinatura && !faltaEnviar) {
    const relogio = setInterval(() => {
      if (!bloco.isConnected) {
        clearInterval(relogio);
        return;
      }
      if (document.visibilityState === 'visible') puxarAgora(a.empresa_id, ['acertos']).catch(() => {});
    }, 20000);
  }
  return bloco;
}

function dialogoAssinarAcerto(a, c) {
  return dialogo((fechar) => {
    const quadro = quadroDeAssinatura();
    const confirmarBotao = h('button', { class: 'botao cheio', type: 'button', disabled: true }, 'Confirmar assinatura');
    quadro.aoMudar(() => {
      confirmarBotao.disabled = !assinaturaSuficiente(quadro.tracos());
    });
    confirmarBotao.addEventListener('click', () => {
      const tracos = quadro.tracos();
      if (!assinaturaSuficiente(tracos)) return;
      quadro.desligar();
      fechar(caminhoDosTracos(tracos));
    });
    return [
      h('h2', null, `Assinatura de ${c?.nome ?? ''}`),
      h('p', null, `Confiro e concordo com o acerto nº ${a.numero}/${a.ano}: ${resultadoDoSaldo(Number(a.saldo), nomeCurto(c))}`),
      h('p', { class: 'pequeno' }, 'Assine com o dedo no quadro:'),
      quadro.elemento,
      h('div', { class: 'acoes' },
        confirmarBotao,
        h('button', { class: 'botao cheio secundario', type: 'button', onClick: () => quadro.limpar() }, 'Limpar e assinar de novo'),
        h('button', { class: 'botao texto', type: 'button', onClick: () => { quadro.desligar(); fechar(null); } }, 'Cancelar')),
    ];
  });
}

// ---------------------------------------------------------------------
// Bloco na tela do colaborador: o que está para acertar e os últimos acertos
// ---------------------------------------------------------------------
export async function blocoAcertosDoColaborador(c, gravavel) {
  const [pend, historico, pessoas] = await Promise.all([pendenteDoColaborador(c.id), acertos.historico(c.id), colaboradores.listar()]);
  const nome = nomeCurto(c);
  const f = pend.fechamento;
  let resumo;
  if (pend.previa.bloqueio) {
    resumo = h('p', { class: 'pequeno' }, `O acerto nº ${pend.previa.bloqueio.numero}/${pend.previa.bloqueio.ano} ainda não foi quitado.`);
  } else if (soConsulta(c, pend.previa)) {
    resumo = h('p', { class: 'pequeno' }, 'Desativado: depois do último acerto, fica só para consulta.');
  } else if (f.vazio) {
    resumo = h('p', { class: 'pequeno' }, pend.emAndamento > 0 ? 'Nada para acertar agora: a viagem em andamento ainda não tem carga.' : 'Nada esperando acerto.');
  } else {
    resumo = h('p', { class: 'pequeno' }, `${textoPendente(f)}. Previsto até hoje: ${resultadoDoSaldo(f.totais.saldo, nome)}`);
  }
  return secao('Acerto', h('div', { class: 'lista' },
    resumo,
    gravavel && !f.vazio && h('a', { class: 'botao', href: `#/acertos/novo?colaborador=${c.id}` }, `Fazer o acerto de ${nome}`),
    pend.previa.bloqueio && h('a', { class: 'botao secundario', href: `#/acertos/${pend.previa.bloqueio.id}` }, `Abrir o acerto nº ${pend.previa.bloqueio.numero}/${pend.previa.bloqueio.ano}`),
    historico.slice(0, 5).map((a) => cartaoAcerto(a, pessoas)),
    historico.length > 5 && h('a', { class: 'botao secundario', href: '#/acertos' }, 'Ver todos os acertos')),
  { contador: historico.filter((a) => !a.deleted_at).length ? String(historico.filter((a) => !a.deleted_at).length) : null });
}
